import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { handleError } from '@shared-functions/handle-error.function';
import { TipoEntidad } from '@entidad/interfaces';
import { GenericResponse } from '@shared-interfaces/generic-response.interface';
import {
  BehaviorSubject,
  Observable,
  of,
  catchError,
  finalize,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TiposEntidadService {
  private API_SERVER = environment.API_SERVER;
  private TIPO_URL = `${this.API_SERVER}/entity-types`;
  private PERM_URL = `${this.API_SERVER}/permission`;

  // Trigger de recarga
  private _refresh$ = new BehaviorSubject<void>(undefined);

  // Loading State
  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoading.asObservable();

  constructor(private http: HttpClient) {}

  tiposEntidades$: Observable<TipoEntidad[]> = this._refresh$.pipe(
    tap(() => this._isLoading.next(true)),
    switchMap(() =>
      this.getAll().pipe(
        catchError((error) => {
          console.error('Error fetching tipos:', error);
          return of([]);
        }),
        finalize(() => this._isLoading.next(false)),
      ),
    ),
    shareReplay(1),
  );

  refresh(): void {
    this._refresh$.next();
  }

  getAll(): Observable<TipoEntidad[]> {
    return this.http.get<TipoEntidad[]>(`${this.TIPO_URL}/`).pipe(catchError(handleError));
  }

  getById(id: number | string): Observable<TipoEntidad> {
    return this.http
      .get<TipoEntidad>(`${this.TIPO_URL}/detalle/${id}`)
      .pipe(catchError(handleError));
  }

  create(dto: any): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(`${this.TIPO_URL}/`, dto).pipe(
      tap(() => this.refresh()), // Al crear, refrescamos la lista automáticamente
      catchError(handleError),
    );
  }

  update(dto: any): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(`${this.TIPO_URL}/${dto.tipoEntidadId}`, dto).pipe(
      tap(() => this.refresh()), // Al editar, refrescamos la lista automáticamente
      catchError(handleError),
    );
  }

  // Permisos...
  getPermisosAsignados(id: number | string): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.PERM_URL}/${id}/tipo-entidad`)
      .pipe(catchError(handleError));
  }

  updatePermisos(id: number, slugs: string[]): Observable<GenericResponse> {
    return this.http
      .put<GenericResponse>(`${this.PERM_URL}/${id}/tipo-entidad`, { data: slugs })
      .pipe(catchError(handleError));
  }
}
